import {CommentModel} from "./commentModel";
import {CommentType, Nullable, VariantAttributeType} from "../../definitions/types";
import {VariantAttributesModel} from "../view/variantAttributesModel";

export class ReviewMediaReviewRatingModel {
    private readonly _id: number;
    private readonly _orderNumber: string;
    private readonly _productId: number;
    private readonly _primaryId?: number;
    private readonly _grade: number;
    private readonly _accountId?: number;
    private readonly _name: string;
    private readonly _visible: boolean;
    private readonly _created: Date;
    private readonly _updated?: Date;
    private readonly _attributes: VariantAttributesModel;
    private readonly _comments: CommentModel[];

    constructor(rating: {
        id: number
        orderNumber: string
        productId: number
        primaryId?: number
        grade: number
        accountId?: number
        name: string
        visible: boolean
        created: Date
        updated?: Date
        attributes: Nullable<VariantAttributeType>[]
        comments: Nullable<CommentType>[]
    }) {
        this._id = rating.id;
        this._orderNumber = rating.orderNumber;
        this._productId = rating.productId;
        this._primaryId = rating.primaryId;
        this._grade = rating.grade;
        this._accountId = rating.accountId;
        this._name = rating.name;
        this._visible = rating.visible;
        this._created = rating.created;
        this._updated = rating.updated;
        this._attributes = new VariantAttributesModel(rating.attributes);
        this._comments = rating.comments
            .filter((comment): comment is CommentType => comment !== null)
            .map(comment => new CommentModel(comment));
    }

    /**
     * @property id
     * @description Returns the id of the rating.
     * @returns {number}
     */
    public get id(): number {
        return this._id;
    }

    /**
     * @property orderNumber
     * @description Returns the order number of the rating.
     * @returns {string}
     */
    public get orderNumber(): string {
        return this._orderNumber;
    }

    /**
     * @property productId
     * @description Returns the product id of the rating.
     * @returns {number}
     */
    public get productId(): number {
        return this._productId;
    }

    /**
     * @property primaryId
     * @description Returns the primary id of the rating.
     * @returns {number | undefined}
     */
    public get primaryId(): number | undefined {
        return this._primaryId;
    }

    /**
     * @property grade
     * @description Returns the grade of the rating.
     * @returns {number}
     */
    public get grade(): number {
        return this._grade;
    }

    /**
     * @property gradeOutOfFive
     * @description Returns the grade of the rating out of five. (i.e. 4/5)
     * @returns {string}
     */
    public get gradeOutOfFive(): string {
        return `${this._grade}/5`
    }

    /**
     * @property accountId
     * @description Returns the account id of the rating.
     * @returns {number | undefined}
     */
    public get accountId(): number | undefined {
        return this._accountId;
    }

    /**
     * @property name
     * @description Returns the name of the rating.
     * @returns {string}
     */
    public get name(): string {
        return this._name;
    }

    /**
     * @property visible
     * @description Returns the visibility of the rating.
     * @returns {boolean}
     */
    public get visible(): boolean {
        return this._visible;
    }

    /**
     * @property created
     * @description Returns the created date of the rating in UTC.
     * @returns {Date}
     */
    public get created(): Date {
        return this._created;
    }

    /**
     * @property createdOn
     * @description Returns the day the rating was created in UTC in the format of Month Day, Year.
     * @returns {string}
     */
    public get createdOn(): string {
        return new Date(this._created).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    /**
     * @property updated
     * @description Returns the updated date of the rating in UTC.
     * @returns {Date | undefined}
     */
    public get updated(): Date | undefined {
        return this._updated;
    }

    /**
     * @property byline
     * @description Returns the byline of the rating. (name + createdOn)
     * @returns {string}
     */
    public get byline(): string {
        return `By ${this._name} - ${this.createdOn}`;
    }

    /**
     * @property comments
     * @description Returns the comments of the rating.
     * @returns {CommentModel[]}
     */
    public get comments(): CommentModel[] {
        return this._comments;
    }

    /**
     * @property attributes
     * @description Returns the attributes of the rating.
     * @returns {VariantAttributesModel}
     */
    public get attributes(): VariantAttributesModel {
        return this._attributes;
    }
}